import { useState } from 'react'
import { PAGE_SIZES, clampPage, needsPager, pageCount, pageNumbers, pageRange, pageSlice } from '../lib/paging'
import { rich } from './hallParts'
import { Icon } from './icons'
import { Select } from './Select'
import { useStaffText } from './StaffText'
import '../styles/pager.css'

export interface PagerProps {
  total: number
  page: number
  size: number
  onPage: (page: number) => void
  onSize: (size: number) => void
  /** Under a grid of cards rather than inside a .table-card: framed on its own. */
  free?: boolean
}

/**
 * One page of a long list. `resetKey` is whatever filters the rows (search,
 * tab): when it changes the list goes back to its first page.
 */
export function usePaged<T>(rows: readonly T[], resetKey = '') {
  const [size, setSize] = useState<number>(PAGE_SIZES[0])
  const [at, setAt] = useState({ key: resetKey, page: 0 })
  const page = clampPage(at.key === resetKey ? at.page : 0, rows.length, size)
  const pager: PagerProps = {
    total: rows.length,
    page,
    size,
    onPage: (p) => setAt({ key: resetKey, page: p }),
    onSize: (s) => {
      setSize(s)
      setAt({ key: resetKey, page: 0 })
    },
  }
  return { rows: pageSlice(rows, page, size), pager }
}

/** The list footer: "Showing 1–10 of 151", page buttons, rows per page. Nothing for an empty list. */
export function Pager({ total, page, size, onPage, onSize, free }: PagerProps) {
  const { say } = useStaffText()
  if (!needsPager(total)) return null
  const { from, to } = pageRange(page, size, total)
  const pages = pageCount(total, size)
  const last = pages - 1
  const num = (n: number) => <b>{n.toLocaleString('en-US')}</b>
  return (
    <nav className={free ? 'pager pager--free' : 'pager'} aria-label={say('ui.pager.label')}>
      <span className="pager__shown">{rich(say, 'ui.pager.showing', {}, { from: num(from), to: num(to), n: num(total) })}</span>
      <span className="pager__nav">
        <button type="button" className="pager__b" aria-label={say('ui.pager.prev')} disabled={page <= 0} onClick={() => onPage(page - 1)}>
          <Icon name="left" size={16} />
        </button>
        {pageNumbers(page + 1, pages).map((n, i) =>
          n === 'gap' ? (
            <span key={-i - 1} className="pager__gap" aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={n}
              type="button"
              className="pager__b"
              aria-label={say('ui.pager.page', { n, of: pages })}
              aria-current={n === page + 1 ? 'page' : undefined}
              onClick={() => n !== page + 1 && onPage(n - 1)}
            >
              {n}
            </button>
          ),
        )}
        <button type="button" className="pager__b" aria-label={say('ui.pager.next')} disabled={page >= last} onClick={() => onPage(page + 1)}>
          <Icon name="right" size={16} />
        </button>
      </span>
      <span className="pager__size">
        <span aria-hidden="true">{say('ui.pager.rows')}</span>
        <Select
          size="sm"
          aria-label={say('ui.pager.rows')}
          value={String(size)}
          onChange={(v) => onSize(Number(v))}
          options={PAGE_SIZES.map((n) => ({ value: String(n), label: String(n) }))}
        />
      </span>
    </nav>
  )
}
